import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Sparkles, 
  Home, 
  Building2, 
  Trees, 
  Store, 
  Truck,
  ArrowRight,
  Leaf
} from "lucide-react";
import vitrerieImg from "@/assets/hero-vitrerie-optimized.webp";
import facadesImg from "@/assets/service-facade-optimized.webp";
import bureauxImg from "@/assets/service-bureaux-optimized.webp";
import espacesVertsImg from "@/assets/service-espaces-verts-optimized.webp";
import boutiquesImg from "@/assets/service-boutiques-optimized.webp";
import industrielImg from "@/assets/service-industriel-optimized.webp";

const services = [
  {
    icon: Sparkles,
    title: "Nettoyage de Vitrerie",
    description: "Vitrines, bureaux, surfaces vitrées en hauteur et d'accès difficile",
    image: vitrerieImg,
    link: "/services/vitrerie"
  },
  {
    icon: Home,
    title: "Nettoyage de Façades",
    description: "Karcher haute pression pour murs, bardages et volets",
    image: facadesImg,
    link: "/services/facades"
  },
  {
    icon: Building2,
    title: "Entretien de Bureaux",
    description: "Nettoyage complet, désinfection, sols et poubelles",
    image: bureauxImg,
    link: "/services/bureaux"
  },
  {
    icon: Trees,
    title: "Espaces Verts",
    description: "Tonte, débroussaillage, taille de haies et entretien",
    image: espacesVertsImg,
    link: "/services/espaces-verts"
  },
  {
    icon: Store,
    title: "Boutiques & Commerces",
    description: "Nettoyage de magasins et surfaces commerciales",
    image: boutiquesImg,
    link: "/services/boutiques"
  },
  {
    icon: Truck,
    title: "Nettoyage Industriel",
    description: "Entrepôts, sites logistiques avec machines spécialisées",
    image: industrielImg,
    link: "/services/industriel"
  }
];

const ServicesGrid = () => {
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 animate-fade-up">
          <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Nos Services Professionnels
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Une gamme complète de services de nettoyage adaptés à vos besoins professionnels et particuliers
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <Link 
              key={index} 
              to={service.link}
              className="block group animate-fade-up hover:scale-[1.02] transition-all duration-300"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <Card className="h-full overflow-hidden border-2 hover:border-primary/50 hover:shadow-xl transition-all duration-300 relative">
                {/* Badge écologique */}
                <Badge 
                  variant="secondary" 
                  className="absolute top-4 right-4 z-10 bg-secondary/90 backdrop-blur-sm text-secondary-foreground flex items-center gap-1"
                >
                  <Leaf className="h-3 w-3" />
                  Éco-responsable
                </Badge>
                
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    width="512"
                    height="512"
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                  {/* Gradient overlay moderne */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent opacity-90"></div>
                  
                  {/* Icône avec effet glassmorphism */}
                  <div className="absolute bottom-4 left-4 bg-background/80 backdrop-blur-md p-3 rounded-xl border border-primary/20 group-hover:bg-primary/20 group-hover:border-primary transition-all duration-300">
                    <service.icon className="h-7 w-7 text-primary" />
                  </div>
                </div>
                
                <CardHeader className="space-y-3">
                  <CardTitle className="text-2xl text-foreground group-hover:text-primary transition-colors flex items-center justify-between">
                    <span>{service.title}</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </CardTitle>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="flex items-center gap-2 text-sm text-primary font-medium group-hover:gap-3 transition-all">
                    <span>Découvrir ce service</span>
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/services">
            <Button size="lg" className="bg-gradient-primary hover:shadow-lg transition-all">
              Voir tous nos services
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
